import { filterClasses } from "@swivl-mono/helpers";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface DataTablePaginationProps<TData> {
  table: any; // Replace 'any' with the actual type of your table if available
  paginationState?: {
    hasNext?: boolean;
    hasPrev?: boolean;
    onNext?: () => void;
    onPrev?: () => void;
  };
}

const DataTablePagination = ({ table, paginationState }: DataTablePaginationProps<any>) => {
  // If cursor pagination is enabled, use custom controls
  if (paginationState) {
    return (
      <div className="flex items-center gap-2 py-2">
        <button
          className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
          onClick={paginationState.onPrev}
          disabled={!paginationState.hasPrev}
        >
          <FiChevronLeft />
        </button>
        <button
          className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
          onClick={paginationState.onNext}
          disabled={!paginationState.hasNext}
        >
          <FiChevronRight />
        </button>
      </div>
    );
  }

  // Default offset-based pagination
  const pageIndex = table.getState().pagination.pageIndex;

  const [pageInputValue, setPageInputValue] = useState(table.getState().pagination.pageIndex + 1);

  // sync page input when user presses next or prev buttons
  useEffect(() => {
    setPageInputValue(pageIndex + 1);
  }, [pageIndex]);

  return (
    <>
      {table.getPageCount() <= 5 ? (
        <div className="flex items-center gap-2">
          <button
            className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <FiChevronLeft />
          </button>
          {Array.from({ length: table.getPageCount() }).map((_, index) => (
            <button
              key={index}
              className={filterClasses(
                "flex h-6 w-6 cursor-pointer items-center justify-center rounded font-mono text-xs",
                "transition-colors duration-150 ease-in-out",
                table.getState().pagination.pageIndex === index
                  ? "border border-orange-500 font-bold text-orange-500"
                  : "font-normal text-grey-900 hover:bg-orange-50"
              )}
              onClick={() => table.setPageIndex(index)}
            >
              {index + 1}
            </button>
          ))}
          <button
            className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <FiChevronRight />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <button
            className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
            onClick={() => {
              table.previousPage();
              setPageInputValue(table.getState().pagination.pageIndex + 1);
            }}
            disabled={!table.getCanPreviousPage()}
          >
            <FiChevronLeft />
          </button>
          <input
            className={filterClasses(
              "font-mono",
              "h-6 w-6 rounded text-center text-xs caret-orange-500",
              "border border-grey-400 hover:border-orange-500 focus-visible:border-orange-500 focus-visible:font-bold focus-visible:outline-none"
            )}
            onChange={(e) => {
              const value = e.target.value;
              setPageInputValue(Number(value));
            }}
            onBlur={(e) => {
              table.setPageIndex(Number(e.target.value) - 1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                table.setPageIndex(Math.max(0, Number(pageInputValue) - 1));
              }
            }}
            value={pageInputValue}
            min={1}
            max={table.getPageCount() + 1}
            inputMode="numeric"
          />
          <span className="body-sm text-grey-900">/</span>
          <span className="flex h-6 w-6 items-center justify-center font-mono text-xs text-grey-900">{table.getPageCount()}</span>
          <button
            className="cursor-pointer rounded-sm px-1 py-1 transition-colors duration-150 ease-in-out hover:bg-orange-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400"
            onClick={() => {
              table.nextPage();
              setPageInputValue(table.getState().pagination.pageIndex + 1);
            }}
            disabled={!table.getCanNextPage()}
          >
            <FiChevronRight />
          </button>
        </div>
      )}
    </>
  );
};

export { DataTablePagination };
